import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext, API } from '@/App';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ArrowLeft, Plus, Trash2, Sparkles, Crown, Pencil, Lock } from 'lucide-react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { validateAndShowError } from '@/utils/contentFilter';
import { canAccessAvatarBuilding, canAddAvatar, getAvatarLimit } from '@/utils/subscriptionUtils';
import GuestModeBanner from '@/components/GuestModeBanner';

const AVATAR_TYPES = ['person', 'animal'];
const ANIMAL_CATEGORIES = {
  'Birds': ['canary', 'eagle', 'flamingo', 'owl', 'parrot', 'peacock', 'sparrow'],
  'Cats': ['maine coon', 'persian cat', 'siamese cat', 'sphynx', 'tabby cat'],
  'Dogs': ['beagle', 'bulldog', 'cocker spaniel', 'dalmatian', 'german shepherd', 'golden retriever', 'husky', 'labrador', 'poodle'],
  'Farm Animals': ['chicken', 'cow', 'duck', 'goat', 'horse', 'pig', 'pony', 'sheep'],
  'Fantasy Creatures': ['dragon', 'griffin', 'mermaid', 'phoenix', 't-rex', 'unicorn'],
  'Small Pets': ['chinchilla', 'ferret', 'hamster', 'guinea pig', 'lizard', 'mouse', 'rabbit', 'turtle'],
  'Wild Animals': ['bear', 'cheetah', 'dolphin', 'elephant', 'giraffe', 'lion', 'rhino', 'tiger', 'wolf']
};


const COLORS = ['black', 'blue', 'brown', 'golden', 'green', 'grey', 'orange', 'pink', 'purple', 'red', 'white', 'yellow'];
const ANIMAL_ACCESSORIES = ['bandana', 'bell', 'bow tie', 'cape', 'collar', 'glasses', 'hat', 'scarf', 'tiara'];
const HAIR_COLORS = ['black', 'blue', 'blonde', 'brown', 'green', 'grey', 'pink', 'purple', 'red', 'white'];
const HAIR_STYLES = ['bald', 'braided', 'curly', 'long', 'ponytail', 'short', 'straight', 'wavy'];
const GENDERS = ['male', 'female', 'neutral'];
const AGE_GROUPS = ['baby', 'toddler', 'young child', 'teenager', 'young adult', 'adult', 'senior'];
const OCCUPATIONS = ['none', 'athlete', 'doctor', 'driver', 'home maker', 'musician', 'nurse', 'office worker', 'shop owner', 'student', 'superhero', 'teacher', 'tradesperson', 'other'];

const CLOTHING_STYLES = ['casual wear', 'dress', 'formal wear', 'jeans and t-shirt', 'party wear', 'pyjamas', 'school uniform', 'sporty outfit', 'traditional attire', 'winter wear'];

const AvatarBuilder = () => {
  const navigate = useNavigate();
  const { user, token, nightMode, refreshUser } = useContext(AuthContext);
  const [avatars, setAvatars] = useState([]);
  const [children, setChildren] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [selectedAvatar, setSelectedAvatar] = useState(null);
  
  const isGuestMode = user?.id === 'guest-user';
  const isAdmin = user?.role === 'admin';
  const userTier = user?.subscription_tier || 'free';
  const isReadOnly = userTier === 'free' && !isAdmin;
  const canAccess = canAccessAvatarBuilding(userTier) || isAdmin;
  const avatarLimit = getAvatarLimit(userTier);
  const canAddMore = canAddAvatar(userTier, avatars.length) || isAdmin;
  const [formData, setFormData] = useState({
    name: '',
    avatar_type: 'animal',
    attributes: {}
  });
  const [generating, setGenerating] = useState(false);
  const [selectedAnimalCategory, setSelectedAnimalCategory] = useState(''); // State for animal category selection
  const [otherAnimal, setOtherAnimal] = useState(''); // State for custom animal input
  const [selectedHairColor, setSelectedHairColor] = useState('');
  const [selectedHairStyle, setSelectedHairStyle] = useState('');

  useEffect(() => {
    fetchAvatars();
    fetchChildren();
  }, []);

  const fetchAvatars = async () => {
    try {
      // Use metadata endpoint with thumbnails for faster loading
      const response = await axios.get(`${API}/avatars/metadata`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { limit: 50 }
      });
      setAvatars(response.data);
    } catch (error) {
      console.error('Failed to fetch avatars:', error);
      // A 404 is expected for new users with no avatar, treat as empty list.
      if (error.response?.status !== 404) {
        toast.error('Failed to load avatars');
      }
    }
  };

  const fetchChildren = async () => {
    try {
      // Use optimized endpoint for names only
      const response = await axios.get(`${API}/children/list/names`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setChildren(response.data);
    } catch (error) {
      console.error('Failed to fetch children:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate avatar name
    if (!validateAndShowError(formData.name, 'Avatar name', toast)) {
      return;
    }
    
    // Validate description if present
    if (formData.attributes.description && !validateAndShowError(formData.attributes.description, 'Description', toast)) {
      return;
    }
    
    // Validate hair if present
    if (formData.attributes.hair && !validateAndShowError(formData.attributes.hair, 'Hair description', toast)) {
      return;
    }
    
    // Validate clothes if present
    if (formData.attributes.clothes && !validateAndShowError(formData.attributes.clothes, 'Clothes description', toast)) {
      return;
    }
    
    try {
      if (selectedAvatar) {
        await axios.patch(`${API}/avatars/${selectedAvatar.id}`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Avatar updated successfully');
      } else {
        await axios.post(`${API}/avatars`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Avatar created successfully');
      }
      setShowDialog(false);
      fetchAvatars();
      resetForm();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to save avatar';
      toast.error(errorMsg);
    }
  };

  const handleDelete = async (avatarId) => {
    if (!window.confirm('Are you sure you want to delete this avatar?')) return;
    
    try {
      await axios.delete(`${API}/avatars/${avatarId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Avatar deleted successfully');
      fetchAvatars();
    } catch (error) {
      toast.error('Failed to delete avatar');
    }
  };

  const handleGenerateImage = async (avatarId) => {
    setGenerating(true);
    try {
      // include avatar attributes and preferred size to reduce generated image bytes
      let avatar = avatars.find(a => a.id === avatarId);
      if (!avatar) {
        const res = await axios.get(`${API}/avatars/${avatarId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        avatar = res.data;
      }

      const { getPreferredImageParams } = await import('@/lib/utils');
      const size = getPreferredImageParams();

      const payload = {
        name: avatar?.name,
        avatar_type: avatar?.avatar_type,
        attributes: avatar?.attributes || {},
        image_size: size
      };

      await axios.post(
        `${API}/avatars/${avatarId}/generate-image`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Image generated successfully!');
      await fetchAvatars();
      
      // Update selected avatar with new image - fetch single avatar to get full data
      const updatedAvatar = await axios.get(`${API}/avatars/${avatarId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (updatedAvatar.data) {
        setSelectedAvatar(updatedAvatar.data);
      }
    } catch (error) {
      toast.error('Failed to generate image' + (error.response?.data?.detail ? `: ${error.response.data.detail}` : ''));
    } finally {
      setGenerating(false);
    }
  };

  const openDialog = (avatar = null) => {
    // Check if user can add more avatars (only when adding new, not editing)
    if (!avatar && !canAddMore) {
      const limitText = avatarLimit === 0 ? 'no avatars' : `${avatarLimit} avatar${avatarLimit > 1 ? 's' : ''}`;
      toast.error(`You've reached your limit of ${limitText} for ${userTier} tier`, {
        action: {
          label: 'Upgrade',
          onClick: () => navigate('/subscription')
        },
        duration: 5000
      });
      return;
    }
    
    if (avatar) {
      setSelectedAvatar(avatar);
      setFormData({
        name: avatar.name,
        avatar_type: avatar.avatar_type,
        attributes: avatar.attributes || {}
      });
      // Initialize selected hair color and style for the UI
      setSelectedHairColor(avatar.attributes?.hair || '');
      setSelectedHairStyle(avatar.attributes?.hair_style || '');
    }
    setShowDialog(true);
  };

  const resetForm = () => {
    setSelectedAvatar(null);
    setSelectedAnimalCategory('');
    setOtherAnimal('');
    setSelectedHairColor('');
    setSelectedHairStyle('');
    setFormData({
      name: '',
      avatar_type: 'animal',
      attributes: {
        hair: '', // Reset hair color
        hair_style: '' // Reset hair style
      }
    });
  };

  const updateAttribute = (key, value) => {
    setFormData(prev => ({
      ...prev,
      attributes: { ...prev.attributes, [key]: value }
    }));
  };

  const handleOtherAnimalAdd = () => {
    const trimmed = otherAnimal.trim().toLowerCase();
    if (!trimmed) {
      toast.error('Please enter an animal type');
      return;
    }
    
    // Basic validation
    const allowedCharsPattern = /^[a-zA-Z0-9\s,.!;-]*$/;
    if (!allowedCharsPattern.test(trimmed)) {
      toast.error('Only letters, numbers, and ,.!;- are allowed');
      return;
    }
    
    updateAttribute('animal_type', trimmed);
    setOtherAnimal('');
    setSelectedAnimalCategory('');
    toast.success('Animal type set');
  };

  return (
    <div className={`min-h-screen ${nightMode ? 'night-gradient' : 'playful-gradient'} page-enter`}>
      {isGuestMode && <GuestModeBanner />}
      <div className="container mx-auto px-4 sm:px-6 py-4 sm:py-6">
        <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
          <Button
            data-testid="back-to-dashboard-btn"
            onClick={() => navigate('/dashboard')}
            variant="outline"
            className="rounded-full flex-shrink-0 w-20"
            style={{
              background: nightMode ? 'var(--bg-secondary)' : 'white',
              color: nightMode ? 'var(--text-primary)' : 'inherit',
              borderColor: nightMode ? 'var(--sketch-border)' : 'inherit'
            }}
          >
            <ArrowLeft className="w-4 h-4 sm:w-5 sm:h-5" />
          </Button>
          <h1 className="text-lg sm:text-2xl font-bold" style={{ fontFamily: 'Fredoka, sans-serif', color: 'var(--text-primary)' }}>
            Manage Other Characters
          </h1>
        </div>

        <div className="mb-4 sm:mb-6">
          <Button
            data-testid="create-avatar-btn"
            onClick={() => openDialog()}
            disabled={isGuestMode || !canAddMore}
            className={`pill-button ${!canAddMore || isGuestMode ? 'opacity-50 cursor-not-allowed' : ''}`}
            style={{ background: 'var(--accent-2, #818cf8)', color: 'var(--accent-foreground, white)' }}
            title={isGuestMode ? 'Sign in to create avatars' : !canAddMore ? 'Upgrade to create more' : ''}
          >
            <Plus className="mr-2 w-5 h-5" />
            Create New Avatar {!canAddMore && <Crown className="ml-2 w-4 h-4" />}
          </Button>
          {!canAddMore && !isGuestMode && (
            <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>
              You&apos;ve reached your limit of {avatarLimit} avatar{avatarLimit > 1 ? 's' : ''}. Upgrade to create more.
            </p>
          )}
          {isGuestMode && (
            <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>
              Sign in to create and manage avatars
            </p>
          )}
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3 sm:gap-4">
          {avatars.map((avatar) => (
            <div key={avatar.id} className="story-card p-2 sm:p-3 flex flex-col" data-testid={`avatar-card-${avatar.id}`}>
              {avatar.image ? (
                <div className="mb-2">
                  <img
                    src={`data:image/png;base64,${avatar.image}`}
                    alt={avatar.name}
                    className="w-full h-24 sm:h-auto sm:aspect-square rounded-lg sm:rounded-full object-cover mx-auto xl:w-32 xl:h-32"
                  />
                </div>
              ) : (
                <div className="mb-2">
                  <div className="w-full h-24 sm:h-auto sm:aspect-square rounded-lg sm:rounded-full flex items-center justify-center mx-auto xl:w-32 xl:h-32" style={{ background: 'var(--accent-5)' }}>
                    <span className="text-2xl sm:text-3xl text-white font-bold">{avatar.name.charAt(0)}</span>
                  </div>
                </div>
              )}
              
              <div className="flex-grow">
                <h3 className="text-sm sm:text-base font-bold mb-0.5 line-clamp-1" style={{ color: 'var(--text-primary)' }}>{avatar.name}</h3>
                <p className="text-xs mb-1.5 sm:mb-2 capitalize hidden sm:block" style={{ color: 'var(--text-secondary)' }}>Type: {avatar.avatar_type}</p>
              </div>
              
              {avatar.attributes && Object.keys(avatar.attributes).length > 0 && (
                <div className="mb-1.5 sm:mb-2 hidden sm:block">
                  <p className="text-xs font-semibold mb-1" style={{ color: 'var(--text-secondary)' }}>
                    Attributes:
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {Object.entries(avatar.attributes)
                      .filter(([, value]) => value !== null && value !== undefined && value !== '')
                      .slice(0, 3)
                      .map(([key, value], idx) => (
                        <span
                          key={idx}
                          className="text-xs px-1.5 py-0.5 rounded-full"
                          style={{ background: 'var(--accent-5)', color: 'white' }}
                        >
                          {value}
                        </span>
                      ))}
                  </div>
                </div>
              )}
              
              <div className="flex items-center gap-1 sm:gap-1.5 mt-auto pt-2">
                <Button
                  data-testid={`edit-avatar-btn-${avatar.id}`}
                  onClick={() => openDialog(avatar)}
                  variant="outline"
                  className="rounded-full flex items-center gap-1"
                  size="sm"
                  style={{
                    background: nightMode ? 'var(--bg-secondary)' : 'white',
                    color: nightMode ? 'var(--text-primary)' : 'inherit',
                    borderColor: nightMode ? 'var(--sketch-border)' : 'inherit'
                  }}
                  title={isGuestMode ? 'View Avatar (Sign in to edit)' : 'Edit Avatar'}
                >
                  <Pencil className="w-3 h-3 sm:w-4 sm:h-4" />                  
                  <span className="text-xs hidden sm:inline">{(isGuestMode || isReadOnly) ? 'View' : 'Edit'}</span>
                </Button>
                <Button
                  data-testid={`generate-avatar-image-tile-${avatar.id}`}
                  onClick={async () => {
                    setGenerating(true);
                    await handleGenerateImage(avatar.id);
                    setGenerating(false);
                  }}
                  disabled={generating || isGuestMode || isReadOnly}
                  variant="outline"
                  size="sm"
                  className={`rounded-full flex items-center gap-1 ${isGuestMode ? 'opacity-50 cursor-not-allowed' : ''}`}
                  style={{
                    background: nightMode ? 'var(--bg-secondary)' : 'white',
                    color: nightMode ? 'var(--text-primary)' : 'inherit',
                    borderColor: nightMode ? 'var(--sketch-border)' : 'inherit'
                  }}
                  title={isGuestMode ? 'Sign in to generate images' : 'Generate Image'}
                >
                  <Sparkles className={`w-3 h-3 sm:w-4 sm:h-4 ${generating ? 'animate-spin' : ''}`} />
                  <span className="text-xs hidden sm:inline">Image</span>
                </Button>
                <Button
                  data-testid={`delete-avatar-btn-${avatar.id}`}
                  onClick={() => handleDelete(avatar.id)}
                  disabled={isGuestMode || isReadOnly}
                  variant="destructive"
                  size="icon"
                  className={`rounded-full ${isGuestMode ? 'opacity-50 cursor-not-allowed' : ''}`}
                  title={isGuestMode ? 'Sign in to delete' : 'Delete Avatar'}
                >
                  <Trash2 className="w-3 h-3 sm:w-4 sm:h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Avatar Form Dialog */}
      <Dialog open={showDialog} onOpenChange={(open) => { setShowDialog(open); if (!open) resetForm(); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-2xl" style={{ fontFamily: 'Fredoka, sans-serif' }}>
              {selectedAvatar ? 'Edit Avatar' : 'Create New Avatar'}
            </DialogTitle>
            <DialogDescription>
              Create a new character or edit an existing one. These avatars can be included in your stories.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-6 mt-4">
            {selectedAvatar && selectedAvatar.image && (
              <div className="flex justify-center mb-4">
                <img
                  src={`data:image/png;base64,${selectedAvatar.image}`}
                  alt={selectedAvatar.name}
                  className="w-32 h-32 rounded-full object-cover"
                />
              </div>
            )}
            <div>
              <Label htmlFor="name">Avatar Name</Label>
              <Input
                data-testid="avatar-name-input"
                id="name"
                required
                value={formData.name}
                disabled={isReadOnly}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="mt-1"
              />
            </div>

            <div>
              <Label>Avatar Type</Label>
              <div className="flex flex-wrap gap-1 mt-1">
                {AVATAR_TYPES.map((type) => (
                  <button
                    key={type}
                    type="button"
                    data-testid={`avatar-type-${type}`}
                    disabled={isReadOnly}
                    onClick={() => setFormData({ ...formData, avatar_type: type, attributes: {} })}
                    className={`tag-button ${formData.avatar_type === type ? 'selected' : ''}`}
                    style={{
                      background: formData.avatar_type === type ? 'var(--accent-2)' : 'var(--bg-secondary)',
                      color: formData.avatar_type === type ? 'white' : 'var(--text-primary)'
                    }}
                  >
                    {type}
                  </button>
                ))}
              </div>
            </div>

            {formData.avatar_type === 'animal' && (
              <>
                <div>
                  <Label>Animal Type</Label>
                  
                  {/* Category Selection */}
                  <div className="flex flex-wrap gap-1 mt-3 mb-3">
                    {Object.keys(ANIMAL_CATEGORIES).map((category) => (
                      <button
                        key={category}
                        type="button"
                        data-testid={`animal-category-${category.toLowerCase().replace(/\s+/g, '-')}`}
                        disabled={isReadOnly}
                        onClick={() => setSelectedAnimalCategory(selectedAnimalCategory === category ? '' : category)}
                        className={`tag-button ${selectedAnimalCategory === category ? 'selected' : ''}`}
                        style={{
                          background: selectedAnimalCategory === category ? 'var(--accent-1)' : 'var(--bg-secondary)',
                          color: selectedAnimalCategory === category ? 'white' : 'var(--text-primary)',
                          fontWeight: '600'
                        }}
                      >
                        {category}
                      </button>
                    ))}
                    <button
                      type="button"
                      data-testid="animal-category-other"
                      disabled={isReadOnly}
                      onClick={() => setSelectedAnimalCategory(selectedAnimalCategory === 'Other' ? '' : 'Other')}
                      className={`tag-button ${selectedAnimalCategory === 'Other' ? 'selected' : ''}`}
                      style={{
                        background: selectedAnimalCategory === 'Other' ? 'var(--accent-1)' : 'var(--bg-secondary)',
                        color: selectedAnimalCategory === 'Other' ? 'white' : 'var(--text-primary)',
                        fontWeight: '600'
                      }}
                    >
                      Other
                    </button>
                  </div>

                  {/* Sub-groups */}
                  {selectedAnimalCategory && selectedAnimalCategory !== 'Other' && (
                    <div 
                      className="p-3 rounded-lg mb-3" 
                      style={{ 
                        border: '2px solid var(--accent-2)', 
                        background: nightMode ? 'rgba(129, 140, 248, 0.1)' : 'rgba(129, 140, 248, 0.05)' 
                      }}
                    >
                      <p className="text-xs font-semibold mb-2" style={{ color: 'var(--text-secondary)' }}>
                        Select from {selectedAnimalCategory}:
                      </p>
                      <div className="flex flex-wrap gap-1">
                        {ANIMAL_CATEGORIES[selectedAnimalCategory].map((animal) => (
                          <button
                            key={animal}
                            type="button"
                            data-testid={`animal-type-${animal}`}
                            disabled={isReadOnly}
                            onClick={() => {
                              updateAttribute('animal_type', animal);
                              setSelectedAnimalCategory('');
                            }}
                            className={`tag-button ${formData.attributes.animal_type === animal ? 'selected' : ''}`}
                            style={{
                              background: formData.attributes.animal_type === animal ? 'var(--accent-5)' : 'var(--bg-secondary)',
                              color: formData.attributes.animal_type === animal ? 'white' : 'var(--text-primary)'
                            }}
                          >
                            {animal}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Other - free text input */}
                  {selectedAnimalCategory === 'Other' && (
                    <div 
                      className="p-3 rounded-lg mb-3" 
                      style={{ 
                        border: '2px solid var(--accent-2)', 
                        background: nightMode ? 'rgba(129, 140, 248, 0.1)' : 'rgba(129, 140, 248, 0.05)' 
                      }}
                    >
                      <p className="text-xs font-semibold mb-2" style={{ color: 'var(--text-secondary)' }}>
                        Enter custom animal type:
                      </p>
                      <div className="flex gap-1">
                        <Input
                          data-testid="other-animal-input"
                          placeholder="e.g., fennec fox, axolotl..."
                          value={otherAnimal}
                          disabled={isReadOnly}
                          onChange={(e) => setOtherAnimal(e.target.value)}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              handleOtherAnimalAdd();
                            }
                          }}
                          maxLength={50}
                        />
                        <Button
                          type="button"
                          data-testid="add-other-animal-btn"
                          onClick={handleOtherAnimalAdd}
                          disabled={isReadOnly}
                          variant="outline"
                          size="sm"
                        >
                          Set
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Selected Animal Display */}
                  {formData.attributes.animal_type && (
                    <div className="mt-3">
                      <p className="text-xs font-semibold mb-2" style={{ color: 'var(--text-secondary)' }}>
                        Selected: <span className="text-sm px-3 py-1 rounded-full" style={{ background: 'var(--accent-5)', color: 'white' }}>
                          {formData.attributes.animal_type}
                        </span>
                      </p>
                    </div>
                  )}
                </div>
                
                <div>
                  <Label>Color (optional)</Label>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {COLORS.map((color) => (
                      <button
                        key={color}
                        type="button"
                        data-testid={`color-${color}`}
                        disabled={isReadOnly}
                        onClick={() => updateAttribute('color', color)}
                        className={`tag-button ${formData.attributes.color === color ? 'selected' : ''}`}
                        style={{
                          background: formData.attributes.color === color ? 'var(--accent-1)' : 'var(--bg-secondary)',
                          color: formData.attributes.color === color ? 'white' : 'var(--text-primary)'
                        }}
                      >
                        {color}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <Label>Accessories (optional)</Label>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {ANIMAL_ACCESSORIES.map((accessory) => (
                      <button
                        key={accessory}
                        type="button"
                        data-testid={`accessory-${accessory}`}
                        disabled={isReadOnly}
                        onClick={() => updateAttribute('accessories', accessory)}
                        className={`tag-button ${formData.attributes.accessories === accessory ? 'selected' : ''}`}
                        style={{
                          background: formData.attributes.accessories === accessory ? 'var(--accent-1)' : 'var(--bg-secondary)',
                          color: formData.attributes.accessories === accessory ? 'white' : 'var(--text-primary)'
                        }}
                      >
                        {accessory}
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}

            {formData.avatar_type === 'person' && (
              <>
                <div>
                  <Label>Gender</Label>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {GENDERS.map((gender) => (
                      <button
                        key={gender}
                        type="button"
                    disabled={isReadOnly}
                        onClick={() => updateAttribute('gender', gender)}
                        className={`tag-button ${formData.attributes.gender === gender ? 'selected' : ''}`}
                        style={{
                          background: formData.attributes.gender === gender ? 'var(--accent-2)' : 'var(--bg-secondary)',
                          color: formData.attributes.gender === gender ? 'white' : 'var(--text-primary)'
                        }}
                      >
                        {gender}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <Label>Age Group</Label>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {AGE_GROUPS.map((age) => (
                      <button
                        key={age}
                        type="button"
                        disabled={isReadOnly}
                        onClick={() => updateAttribute('age_group', age)}
                        className={`tag-button ${formData.attributes.age_group === age ? 'selected' : ''}`}
                        style={{
                          background: formData.attributes.age_group === age ? 'selected' : 'var(--bg-secondary)',
                          color: formData.attributes.age_group === age ? 'white' : 'var(--text-primary)'
                        }}
                      >
                        {age}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <Label>Occupation</Label>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {OCCUPATIONS.map((occupation) => (
                      <button
                        key={occupation}
                        type="button"
                        disabled={isReadOnly}
                        onClick={() => updateAttribute('occupation', occupation)}
                        className={`tag-button ${formData.attributes.occupation === occupation ? 'selected' : ''}`}
                        style={{
                          background: formData.attributes.occupation === occupation ? 'var(--accent-3)' : 'var(--bg-secondary)',
                          color: formData.attributes.occupation === occupation ? 'white' : 'var(--text-primary)'
                        }}
                      >
                        {occupation}
                      </button>
                    ))}
                  </div>
                </div>
                {formData.attributes.occupation === 'other' && (
                  <div 
                    className="p-3 rounded-lg" 
                    style={{ 
                      border: '2px solid var(--accent-2)', 
                      background: nightMode ? 'rgba(129, 140, 248, 0.1)' : 'rgba(129, 140, 248, 0.05)' 
                    }}
                  >
                    <Label htmlFor="description">Custom Occupation Description</Label>
                    <Input
                      id="description"
                      value={formData.attributes.description || ''}
                      disabled={isReadOnly}
                      onChange={(e) => updateAttribute('description', e.target.value)}
                      placeholder="e.g., astronaut, chef, artist (letters, numbers, ,.!; only)"
                      className="mt-1"
                      maxLength={50}
                    />
                  </div>
                )}
                
                <div>
                  <Label>Hair (optional)</Label>
                  <div className="space-y-3 mt-1">
                    <div>
                      <p className="text-xs font-semibold mb-2" style={{ color: 'var(--text-secondary)' }}>Hair Color:</p>
                      <div className="flex flex-wrap gap-1">
                        {HAIR_COLORS.map((color) => (
                          <button
                            key={color}
                            type="button"
                            data-testid={`hair-color-${color}`}
                            disabled={isReadOnly}
                            onClick={() => {
                              setSelectedHairColor(color);
                              updateAttribute('hair', color); // Set hair color directly
                            }}
                            className={`tag-button ${selectedHairColor === color ? 'selected' : ''}`}
                            style={{
                              background: selectedHairColor === color ? 'var(--accent-3)' : 'var(--bg-secondary)',
                              color: selectedHairColor === color ? 'white' : 'var(--text-primary)'
                            }}
                          >
                            {color}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <p className="text-xs font-semibold mb-2" style={{ color: 'var(--text-secondary)' }}>Hair Style:</p>
                      <div className="flex flex-wrap gap-1">
                        {HAIR_STYLES.map((style) => (
                          <button
                            key={style}
                            type="button"
                            data-testid={`hair-style-${style}`}
                            disabled={isReadOnly}
                            onClick={() => {
                              setSelectedHairStyle(style);
                              updateAttribute('hair_style', style); // Set hair style directly
                            }}
                            className={`tag-button ${selectedHairStyle === style ? 'selected' : ''}`}
                            style={{
                              background: selectedHairStyle === style ? 'var(--accent-3)' : 'var(--bg-secondary)',
                              color: selectedHairStyle === style ? 'white' : 'var(--text-primary)'
                            }}
                          >
                            {style}
                          </button>
                        ))}
                      </div>
                    </div>
                    {(formData.attributes.hair || formData.attributes.hair_style) && (
                      <p className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                        Selected: <span className="font-semibold">
                          {formData.attributes.hair_style && `${formData.attributes.hair_style} `}
                          {formData.attributes.hair}
                        </span>
                      </p>
                    )}
                  </div>
                </div>
                
                <div>
                  <Label>Clothes (optional)</Label>
                  <div className="flex flex-wrap gap-1 mt-1">
                    {CLOTHING_STYLES.map((clothing) => (
                      <button
                        key={clothing}
                        type="button"
                        data-testid={`clothing-${clothing}`}
                        disabled={isReadOnly}
                        onClick={() => updateAttribute('clothes', clothing)}
                        className={`tag-button ${formData.attributes.clothes === clothing ? 'selected' : ''}`}
                        style={{
                          background: formData.attributes.clothes === clothing ? 'var(--accent-4)' : 'var(--bg-secondary)',
                          color: formData.attributes.clothes === clothing ? 'white' : 'var(--text-primary)'
                        }}
                      >
                        {clothing}
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}

            <div className="flex gap-1">
              <Button
                data-testid="generate-avatar-image-dialog-btn"
                type="button"
                onClick={async () => {
                  if (selectedAvatar) {
                    setGenerating(true);
                    await handleGenerateImage(selectedAvatar.id);
                    setGenerating(false);
                  } else {
                    toast.error('Please save the avatar first before generating image');
                  }
                }}
                disabled={generating || !selectedAvatar || isGuestMode || isReadOnly}
                variant="outline"
                className={`flex-1 rounded-full ${isGuestMode ? 'opacity-50 cursor-not-allowed' : ''}`}
                title={isGuestMode ? 'Sign in to generate images' : ''}
              >
                {generating ? (
                  <>
                    <Sparkles className="w-4 h-4 mr-1 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-1" />
                    Generate Image
                  </>
                )}
              </Button>
              <Button
              data-testid="save-avatar-btn"
              type="submit"
              disabled={isGuestMode || isReadOnly}
              className={`flex-1 pill-button ${isGuestMode ? 'opacity-50 cursor-not-allowed' : ''}`}
              style={{ background: (isGuestMode || isReadOnly) ? 'var(--text-secondary)' : 'var(--accent-2)', color: 'white' }}
              title={isGuestMode ? 'Sign in to save avatars' : ''}
              onClick={(e) => {
                if (isGuestMode) {
                  e.preventDefault();
                  toast.info('Sign in to save your avatar changes!', {
                    action: {
                      label: 'Sign In',
                      onClick: () => {
                        const redirectUrl = `${window.location.origin}/dashboard`;
                        const authUrl = process.env.REACT_APP_AUTH_URL || 'https://auth.emergentagent.com';
                        window.location.href = `${authUrl}/?redirect=${encodeURIComponent(redirectUrl)}`;
                      }
                    }
                  });
                }
              }}
            >
              {isGuestMode ? 'Sign in to Save' : isReadOnly ? 'Upgrade to Save' : (selectedAvatar ? 'Update Avatar' : 'Create Avatar')}
            </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AvatarBuilder;